import produce from 'immer';
import { get, set, isEmpty } from 'lodash';
import { lruMemoize } from 'reselect';
import type { BBModel, BBSymbolLink, CardData, CardEditorType, SelectedBBSingle, StorySymbols } from 'types/story';
import { getNestedSymbols, isSymbol } from 'utils/blocks/symbol';
import { findComponentBy } from 'utils/blocks/find-component-by';
import { isLayerType } from 'utils/blocks/is-layer-type';
import { getSymbolModel } from 'utils/blocks/get-symbol-model';

// get element by 'id'. used as more expensive, but robust fallback, instead of get by 'path'
function findById(collection: BBModel[], id: string) {
	return findComponentBy(collection, { path: '_id', value: id })?.component;
}

function getSelectedSymbolModel(props: { symbol: BBSymbolLink; symbols: StorySymbols; elementId: string }) {
	const fullSymbolModel = getSymbolModel(props.symbol, props.symbols);
	return fullSymbolModel ? findById([fullSymbolModel], props.elementId) : undefined;
}

type GetSelectedElementModelProps = {
	element: SelectedBBSingle;
	cardData: CardData;
	symbols?: StorySymbols;
	storyElements: CardEditorType['storyElements'];
};

// Get entire original model of selected element
function getSelectedElementModelFn(props: GetSelectedElementModelProps): {
	model: BBModel | undefined;
	hasNestedSymbols: boolean;
} {
	const { path, _id } = props.element;
	const hasSymbols = !isEmpty(props.symbols);
	const result: ReturnType<typeof getSelectedElementModelFn> = {
		model: undefined,
		hasNestedSymbols: false,
	};

	// story element
	if (isLayerType(props.element).global) {
		result.model = get(props.storyElements, path) || findById(props.storyElements, _id);
		return result;
	}

	// symbol
	if (isSymbol(props.element) && hasSymbols) {
		const symbolModel = getSelectedSymbolModel({
			symbol: props.element,
			symbols: props.symbols!,
			elementId: _id,
		});

		if (symbolModel) {
			result.model = symbolModel;
			return result;
		}
	}

	let el = get(props.cardData.elements, path) as BBModel | undefined;
	if (!el || (el._id !== undefined && el._id !== props.element._id)) {
		el = findById(props.cardData.elements, _id);
	}

	// collect models of nested symbols
	if (el && el?.children && hasSymbols) {
		getNestedSymbols(el.children).forEach(nestedSymbol => {
			if (!result.hasNestedSymbols) {
				result.hasNestedSymbols = true;
			}

			el = produce(el, draft => {
				const symbolModel = getSelectedSymbolModel({
					symbol: nestedSymbol.component,
					symbols: props.symbols!,
					elementId: nestedSymbol.component.symbol.instanceId,
				});
				// replace child symbol link with a symbol instance model
				if (symbolModel && draft) set(draft, `children.${nestedSymbol.path}`, symbolModel);
			});
		});
	}

	// simple card element
	result.model = el;

	return result;
}

const isEqual = (a: GetSelectedElementModelProps, b: GetSelectedElementModelProps) => a.element === b.element;

const memoizedGetSelectedElementModel = lruMemoize(getSelectedElementModelFn, isEqual);

export const getSelectedElementModel = memoizedGetSelectedElementModel;
